/**
 * useAdminResourceAuthors Hooks (T064 - US5)
 * React Query hooks for resource authors CRUD with optimistic updates
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminResourceAuthorsApi } from '../services/adminResourceAuthorsApi';
import type {
  AdminResourceAuthorItem,
  CreateResourceAuthorPayload,
  UpdateResourceAuthorPayload,
} from '../types/admin-resource.type';

type ListParams = {
  search?: string;
};

export const resourceAuthorKeys = {
  all: ['admin', 'resource-authors'] as const,
  lists: () => [...resourceAuthorKeys.all, 'list'] as const,
  list: (params?: ListParams) =>
    [...resourceAuthorKeys.lists(), params] as const,
};

export function useResourceAuthors(
  params?: ListParams,
): UseQueryResult<AdminResourceAuthorItem[], Error> {
  return useQuery({
    queryKey: resourceAuthorKeys.list(params),
    queryFn: () => adminResourceAuthorsApi.listResourceAuthors(params),
    staleTime: 300000,
  });
}

export function useCreateResourceAuthor(): UseMutationResult<
  AdminResourceAuthorItem,
  Error,
  CreateResourceAuthorPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminResourceAuthorsApi.createResourceAuthor,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: resourceAuthorKeys.lists(),
      });
    },
  });
}

export function useUpdateResourceAuthor(): UseMutationResult<
  AdminResourceAuthorItem,
  Error,
  UpdateResourceAuthorPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      adminResourceAuthorsApi.updateResourceAuthor(id, payload),
    onMutate: async (updated) => {
      await queryClient.cancelQueries({
        queryKey: resourceAuthorKeys.lists(),
      });
      const queries = queryClient.getQueriesData<AdminResourceAuthorItem[]>({
        queryKey: resourceAuthorKeys.lists(),
      });
      const snapshots = queries.map(([key, data]) => [key, data] as const);

      queryClient.setQueriesData<AdminResourceAuthorItem[]>(
        { queryKey: resourceAuthorKeys.lists() },
        (old) =>
          old?.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      );

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: resourceAuthorKeys.lists(),
      });
    },
  });
}

export function useDeleteResourceAuthor(): UseMutationResult<
  void,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminResourceAuthorsApi.deleteResourceAuthor,
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: resourceAuthorKeys.lists(),
      });
      const queries = queryClient.getQueriesData<AdminResourceAuthorItem[]>({
        queryKey: resourceAuthorKeys.lists(),
      });
      const snapshots = queries.map(([key, data]) => [key, data] as const);

      queryClient.setQueriesData<AdminResourceAuthorItem[]>(
        { queryKey: resourceAuthorKeys.lists() },
        (old) => old?.filter((a) => a.id !== id),
      );

      return { snapshots };
    },
    onError: (_err, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: resourceAuthorKeys.lists(),
      });
    },
  });
}
